import ActionButton from "@/components/buttons/ActionButton";
import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import AntIcon from "@/components/icons/AntIcon";
import { formatDateRange, formatLocaleDate } from "@/utils/formatUtils";
import { Descriptions, Flex, Form, Modal, Popover } from "antd";
import { DescriptionsItemType } from "antd/es/descriptions";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration } from "../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { formatCommissionsLevelName } from "../../../commissions/level/utils";
import { EnumerationType } from "../../../enumerations/enums";
import { CommonAgentFilterRequest } from "../../types";
import { AgentExtendedFilterFormFields } from "./AgentExtendedFilterFormFields";

/** `includeZeroBalance` is the only listing-specific field the modal knows about (debt accounts). */
type ParentFormValues = CommonAgentFilterRequest & { includeZeroBalance?: boolean };

type ModalFieldsForm = Pick<
  CommonAgentFilterRequest,
  | "commissionsSettingsLevelIds"
  | "commissionsSettingsRankIds"
  | "commissionsSettingsStatuses"
  | "statuses"
  | "secondaryStatuses"
  | "completedEducationIds"
  | "completedEducationDateMin"
  | "completedEducationDateMax"
  | "completedAcademies"
  | "completedAcademyDateMin"
  | "completedAcademyDateMax"
  | "agentCreatedAtDateMin"
  | "onlyDirectSubordinates"
  | "onlyRealtySpecialists"
  | "onlyLoanSpecialists"
  | "includeDeactivated"
  | "includeRepresentatives"
  | "includeNonGainers"
>;

/**
 * `additionalFields` lists the remaining form fields of a criterion displayed as one item but stored
 * in several (date ranges), so that clearing the filter resets all of them.
 */
type FilterResultItem = DescriptionsItemType & { additionalFields?: (keyof ModalFieldsForm)[] };

const filterItem = (
  key: string,
  label: string,
  children?: string,
  additionalFields?: (keyof ModalFieldsForm)[],
): FilterResultItem | undefined => (children ? { key, label, children, additionalFields } : undefined);

const dateRangeFilterItem = (
  key: string,
  label: string,
  additionalFields: [keyof ModalFieldsForm, keyof ModalFieldsForm],
  dateMin?: string,
  dateMax?: string,
): FilterResultItem | undefined =>
  dateMin && dateMax ? filterItem(key, label, formatDateRange(dateMin, dateMax), additionalFields) : undefined;

type Props = {
  onSubmit: (values: ModalFieldsForm) => void;
  includeZeroBalance?: boolean;
};

export const AgentExtendedFilterModalForm = ({ onSubmit, includeZeroBalance }: Props) => {
  const { t } = useTranslation();

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const isTopAgentPlus = useIsCurrentAgentTypePlus();
  const commissionsLevelsEnums = useEnumeration(EnumerationType.COMMISSIONS_SETTINGS_LEVELS);
  const ranksEnums = useEnumeration(EnumerationType.COMMISSIONS_SETTINGS_RANKS);
  const educationEnums = useEnumeration(EnumerationType.AGENT_EDUCATIONS);

  const parentFormValues = Form.useFormInstance<ParentFormValues>().getFieldsValue();
  const [form] = Form.useForm<ModalFieldsForm>();

  useEffect(() => {
    if (isFilterModalOpen) {
      form.setFieldsValue(parentFormValues);
    }
  }, [isFilterModalOpen, form, parentFormValues]);

  const yesLabel = t("common.yes");
  const activeFilterItems = [
    filterItem(
      "commissionsSettingsLevelIds",
      t("agent.filter.commissionsSettingsLevelIds"),
      parentFormValues.commissionsSettingsLevelIds
        ?.map(
          (levelId) => formatCommissionsLevelName(commissionsLevelsEnums.find((level) => level.id === levelId)) ?? "",
        )
        .join(", "),
    ),
    filterItem(
      "commissionsSettingsRankIds",
      t("agent.filter.commissionsSettingsRankIds"),
      parentFormValues.commissionsSettingsRankIds
        ?.map((rankId) => ranksEnums.find((rank) => rank.id === rankId)?.name ?? "")
        .join(", "),
    ),
    filterItem(
      "agentCreatedAtDateMin",
      t("agent.filter.agentCreatedAtDateMin"),
      parentFormValues.agentCreatedAtDateMin ? formatLocaleDate(parentFormValues.agentCreatedAtDateMin) : undefined,
    ),
    filterItem(
      "commissionsSettingsStatuses",
      t("commissions.settings.enums.commissionsSettingsStatus._label"),
      parentFormValues.commissionsSettingsStatuses
        ?.map((status) => t(`commissions.settings.enums.commissionsSettingsStatus.${status}`))
        .join(", "),
    ),
    filterItem(
      "statuses",
      isTopAgentPlus ? t("agent.helpers.agentStatusSFAPlusLabel") : t("agent.enums.agentStatus._label"),
      parentFormValues.statuses?.map((status) => t(`agent.enums.agentStatus.${status}`)).join(", "),
    ),
    filterItem(
      "secondaryStatuses",
      isTopAgentPlus
        ? t("agent.helpers.agentSecondaryStatusSFAPlusLabel")
        : t("agent.enums.agentSecondaryStatus._label"),
      parentFormValues.secondaryStatuses?.map((status) => t(`agent.enums.agentSecondaryStatus.${status}`)).join(", "),
    ),
    filterItem(
      "onlyDirectSubordinates",
      t("agent.filter.onlyDirectSubordinates"),
      parentFormValues.onlyDirectSubordinates ? yesLabel : undefined,
    ),
    filterItem(
      "includeDeactivated",
      t("agent.filter.includeDeactivated"),
      parentFormValues.includeDeactivated ? yesLabel : undefined,
    ),
    filterItem(
      "includeRepresentatives",
      t("agent.filter.includeRepresentatives"),
      parentFormValues.includeRepresentatives ? yesLabel : undefined,
    ),
    filterItem(
      "completedEducationIds",
      t("agent.filter.completedEducationIds"),
      parentFormValues.completedEducationIds
        ?.map((id) => educationEnums.find((item) => item.id === id)?.name ?? "")
        .join(", "),
    ),
    dateRangeFilterItem(
      "completedEducationDateRange",
      t("agent.filter.completedEducationDateRange"),
      ["completedEducationDateMin", "completedEducationDateMax"],
      parentFormValues.completedEducationDateMin,
      parentFormValues.completedEducationDateMax,
    ),
    filterItem(
      "completedAcademies",
      t("agent.filter.completedAcademies"),
      parentFormValues.completedAcademies?.map((type) => t(`agent.enums.agentEducationAcademyType.${type}`)).join(", "),
    ),
    dateRangeFilterItem(
      "completedAcademyDateRange",
      t("agent.filter.completedAcademyDateRange"),
      ["completedAcademyDateMin", "completedAcademyDateMax"],
      parentFormValues.completedAcademyDateMin,
      parentFormValues.completedAcademyDateMax,
    ),
    filterItem(
      "includeNonGainers",
      t("agent.filter.includeNonGainers"),
      parentFormValues.includeNonGainers ? yesLabel : undefined,
    ),
    filterItem(
      "onlyRealtySpecialists",
      t("agent.filter.onlyRealtySpecialists"),
      parentFormValues.onlyRealtySpecialists ? yesLabel : undefined,
    ),
    filterItem(
      "onlyLoanSpecialists",
      t("agent.filter.onlyLoanSpecialists"),
      parentFormValues.onlyLoanSpecialists ? yesLabel : undefined,
    ),
    filterItem(
      "includeZeroBalance",
      t("commissions.debtAccount.filter.includeZeroBalance"),
      parentFormValues.includeZeroBalance ? yesLabel : undefined,
    ),
  ].filter((item) => item !== undefined);

  return (
    <>
      <AgentExtendedFilterFormFields hidden includeZeroBalance={includeZeroBalance} />

      <Flex align="center">
        <SecondaryButton icon={<AntIcon type="search" />} onClick={() => setIsFilterModalOpen(true)}>
          {t("common.extendedFilter")}
        </SecondaryButton>

        <div className="margin-left-small">
          {activeFilterItems.length ? (
            <>
              <Popover
                placement="bottom"
                title={t("agent.helpers.activeFilters")}
                content={<Descriptions bordered size="small" column={1} items={activeFilterItems} />}
              >
                <span className="sub-header-info margin-right-tiny cursor-pointer">
                  {t("agent.helpers.extendedFilterActiveHint")} <AntIcon type="question" />
                </span>
              </Popover>

              <ActionButton
                variant="text"
                color="danger"
                icon="delete"
                onClick={() => {
                  const clearedValues: Record<string, false | undefined> = {};

                  activeFilterItems.forEach((item) => {
                    [item.key as keyof ModalFieldsForm, ...(item.additionalFields ?? [])].forEach((field) => {
                      clearedValues[field] = parentFormValues[field] === true ? false : undefined;
                    });
                  });

                  onSubmit(clearedValues as ModalFieldsForm);
                }}
              >
                {t("common.cancel")}
              </ActionButton>
            </>
          ) : undefined}
        </div>
      </Flex>

      <Modal
        width={ModalSizes.MEDIUM}
        open={isFilterModalOpen}
        title={t("common.extendedFilter")}
        onCancel={() => setIsFilterModalOpen(false)}
        onOk={() =>
          form.validateFields().then((values) => {
            onSubmit(values);
            setIsFilterModalOpen(false);
          })
        }
        okText={t("contract.filter.submit")}
        okButtonProps={{ icon: <AntIcon type="search" /> }}
        cancelText={t("common.cancel")}
        afterClose={() => form.resetFields()}
        mask={{ closable: false }}
      >
        <Form form={form} layout="vertical" name="commonFilterFieldsForm">
          <div style={{ display: "grid", gap: rowGutter, alignItems: "end", gridTemplateColumns: "1fr 1fr" }}>
            <AgentExtendedFilterFormFields includeZeroBalance={includeZeroBalance} />
          </div>
        </Form>
      </Modal>
    </>
  );
};
